import { BindableValue } from '../../misc/BindableValue/BindableValue';
import { Disposable } from '../../utils/DisposableContainer/DisposableContainer';
import { RateLimit } from '../../utils/RateLimit/RateLimit';
import { KeyValueStorage, TableName } from '../KeyValueStorage';

/**
 * T = the type of the current value
 * TSerialized = a serialized type which needs to be converted to the actual value
 * TOldSerialized = an old stored value type which needs to be converted to the new type
 */
export class StoredValue<TValue, TSerialized = TValue, TOldSerialized = TSerialized> {
  private readonly bindableValueInfo: BindableValue<ValueInfo<TValue>>;
  private readonly saveValueRateLimited = RateLimit.rateLimitFunction(this.saveValue.bind(this), 250);
  constructor(private readonly storageInfo: StorageInfo, private readonly options: StoredValueOptions<TValue, TSerialized, TOldSerialized>) {
    this.bindableValueInfo = new BindableValue<ValueInfo<TValue>>({
      noValueAvailableErrorMessage: 'no valueInfo available, is the StoredValue initialized?'
    });
  }
  public async init(generateFallbackValueInfo: () => ValueInfo<TValue>): Promise<void> {
    const storedValueInfo = await KeyValueStorage.getItem<ValueInfo<TSerialized> | undefined>(this.storageInfo.key, this.storageInfo.tableName);
    let valueInfo: ValueInfo<TValue>;
    if (storedValueInfo) {
      valueInfo = this.processStoredValueInfo(storedValueInfo);
    } else {
      valueInfo = generateFallbackValueInfo();
    }
    this.bindableValueInfo.setValue(valueInfo);
    if (valueInfo.version !== storedValueInfo?.version) {
      await this.saveValue();
    }
  }
  public async flush(): Promise<void> {
    if (this.saveValueRateLimited.isPending()) {
      this.saveValueRateLimited.cancel();
      await this.saveValue();
    }
  }
  public bindValue(callback: (value: TValue) => void): Disposable {
    return this.bindableValueInfo.bindValue(info => callback(info.value));
  }
  public onValueChanged(callback: (value: TValue) => void): Disposable {
    return this.bindableValueInfo.onValueChanged(info => callback(info.value));
  }
  public getValue(): TValue {
    return this.bindableValueInfo.getValue().value;
  }

  /**
   * Only new instances will be accepted. If you pass value which is the same instance as the already set value, then nothing will happen.
   */
  public setValue(value: TValue): void {
    if (value === this.bindableValueInfo.getValue().value) {
      return;
    }
    this.bindableValueInfo.setValue({
      ...this.bindableValueInfo.getValue(),
      value
    });
    this.saveValueRateLimited();
  }
  private processStoredValueInfo(storedValueInfo: ValueInfo<TSerialized | TOldSerialized>): ValueInfo<TValue> {
    let migratedInfo: ValueInfo<TSerialized>;
    if (this.options.migrateValue) {
      migratedInfo = this.options.migrateValue(storedValueInfo);
    } else {
      // We have to cast to any here because theoretically one could set a TOldSerialized which is different from TSerialized and not provide a migrateValue function.
      // This case is ignored because we can't check that here.
      migratedInfo = storedValueInfo as ValueInfo<TSerialized>;
    }
    if ('deserializeValue' in this.options) {
      return {
        ...migratedInfo,
        value: this.options.deserializeValue(migratedInfo.value)
      };
    }

    // We have to cast to any here because theoretically one could set a TSerialized which is different from TValue and not provide a deserializeValue function.
    // This case is ignored because we can't check that here.
    return migratedInfo as unknown as ValueInfo<TValue>;
  }
  private saveValue(): Promise<void> {
    const valueInfo = this.bindableValueInfo.getValue();
    let serializedValueInfo: ValueInfo<TSerialized>;
    if ('serializeValue' in this.options) {
      serializedValueInfo = {
        ...valueInfo,
        value: this.options.serializeValue(valueInfo.value)
      };
    } else {
      // We have to cast to any here because theoretically one could set a TSerialized which is different from TValue and not provide a deserializeValue function.
      // This case is ignored because we can't check that here
      serializedValueInfo = valueInfo as unknown as ValueInfo<TSerialized>;
    }
    return KeyValueStorage.setItem(this.storageInfo.key, serializedValueInfo, this.storageInfo.tableName);
  }
}
type WithSerializeOptions<TValue, TSerialized, TOptions> = TOptions | (TOptions & {
  /**
   * Converts a serialized value to the target value.
   * By default the stored value will be directly used as the value.
   */
  deserializeValue: (valueInfo: TSerialized) => TValue;
  /**
   * By default the stored value will be directly used as the value.
   */
  serializeValue: (ValueInfo: TValue) => TSerialized;
});
export type StoredValueOptions<TValue, TSerialized, TOldSerialized = TSerialized> = WithSerializeOptions<TValue, TSerialized, {
  /**
   * If the stored value needed to be migrated, then you should increase the version.
   */
  migrateValue?: (valueInfo: ValueInfo<TOldSerialized | TSerialized>) => ValueInfo<TSerialized>;
}>;
export type StorageInfo = {
  key: string;
  tableName?: TableName;
};
export type ValueInfo<T> = {
  version: number;
  value: T;
};